import { reactive, shallowRef } from 'vue'
import { createRouter, createWebHistory, Router, RouteRecordRaw, RouterScrollBehavior } from 'vue-router'

export const pageInitialPosition = shallowRef({ left: 0, top: 0 })

type setupRouterOptions = {
  base: string,
  scrollBehavior?: RouterScrollBehavior,
  addRouterHooks?: (router: Router) => void
}

/**
 * local route loading timeout
 */
let routeloadingTimeout: ReturnType<typeof setTimeout>

/**
 * A default naive implementation of a scrollbehavior
 */
const defaultScrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
  pageInitialPosition.value = savedPosition || { left: 0, top: 0 }
  return to.name !== 'Project' ? pageInitialPosition.value : false
}

export const routes: RouteRecordRaw[] = [
  {
    name: 'Home',
    path: '/',
    meta: { scrollProgress: true },
    component: () => import('./pages/HomePage.vue')
  },
  {
    name: 'About',
    path: '/about',
    meta: { scrollProgress: true },
    component: () => import('./pages/AboutPage.vue')
  },
  {
    name: 'Contact',
    path: '/contact',
    component: () => import('./pages/ContactPage.vue')
  },
  {
    name: 'CollabHub',
    path: '/collab-hub',
    component: () => import('./pages/CollabHubPage.vue')
  },
  {
    name: 'Newsletter',
    path: '/newsletter',
    component: () => import('./pages/NewsletterPage.vue')
  },
  {
    name: 'Works',
    path: '/works/:category?',
    props: true,
    component: () => import('./pages/WorksPage.vue')
  },
  {
    name: 'Prototypes',
    path: '/demos/:category?',
    props: true,
    component: () => import('./pages/PrototypesPage.vue')
  },
  {
    path: '/project/:slug',
    props: true,
    component: () => import('./components/atoms/FlipView.vue'),
    children: [
      {
        name: 'Project',
        path: '',
        meta: { scrollProgress: true },
        props: route => route.params,
        component: () => import('./pages/ProjectPage.vue')
      }
    ]
  },
  {
    name: 'People',
    path: '/people/:slug/:category?',
    props: true,
    component: () => import('./pages/PeoplePage.vue')
  },
  {
    name: 'Press',
    path: '/press',
    component: () => import('./pages/PressPage.vue')
  },
  {
    name: 'Universe',
    path: '/universe',
    meta: {
      postfx: true,
      cameraConfig: 'UNIVERSE'
    },
    component: () => import('./pages/UniversePage.vue')
  },
  {
    // matches /anyroute (if not found) and redirects it to /
    // https://next.router.vuejs.org/guide/migration/#removed-star-or-catch-all-routes
    path: '/:pathMatch(.*)*',
    // flag this route as a 404 page
    // so that (optional) internationalization can replace
    // this with its own implementation
    meta: reactive({ is404: true }),
    redirect: to => {
      return { path: '/' }
    }
  }
]

/**
 * Returns a router instance with attached hooks for tracking page loads,
 * also accepts a custom scrollBehavior
 **/
export const setupRouter = (
  routes: RouteRecordRaw[],
  { base, scrollBehavior, addRouterHooks }: setupRouterOptions
) => {
  /**
   * HASH NAVIGATION
   *
   * When hash mode navigation is required, uncomment the following
   * code and switch to hash history when creating router below
   */
  // If the hash is missing but a pathname is defined this will be used as hash (/foo --> /#/foo)
  // if (!window.location.hash && window.location.pathname) {
  //   history.replaceState(null, null, `/#${window.location.href.replace(window.location.origin, '')}`)
  // }

  const router = createRouter({
    // Provide the history implementation to use
    // history: createWebHashHistory(),
    // or
    history: createWebHistory(base),
    routes, // short for `routes: routes`
    // scrollBehavior: scrollBehavior || defaultScrollBehavior
    scrollBehavior: defaultScrollBehavior
  })

  addRouterHooks && addRouterHooks(router)

  router.beforeEach(async (to, from, next) => {
    // 100ms timeout
    routeloadingTimeout = setTimeout(() => {
      // console.log('route has been loading for more than 100ms, mark is as "loadingChunk"')
      if (!to.meta) to.meta = reactive({ loadingChunk: true })
      to.meta.loadingChunk = true
      if (!from.meta) from.meta = reactive({ loadingChunk: true })
      from.meta.loadingChunk = true
    }, 100)

    // console.time('route chunk loading time')
    next()
  })

  router.beforeResolve((to, from, next) => {
    clearTimeout(routeloadingTimeout)

    if (!to.meta) to.meta = reactive({ loadingChunk: false })
    to.meta.loadingChunk = false
    if (!from.meta) from.meta = reactive({ loadingChunk: false })
    from.meta.loadingChunk = false

    // console.timeEnd('route chunk loading time')
    next()
  })
  return router
}
